/**
 * AP-46 6. — **Kanonischer Stand und Inhalts-Hash der Baustellendokumente.**
 *
 * Eine Funktion für beide Seiten: Das Gerät bildet den Hash beim Einfrieren, der
 * Server rechnet ihn beim Eintreffen nach und lehnt eine Abweichung ab, statt sie zu
 * korrigieren. Bitgleichheit entsteht nur aus **derselben** Funktion — deshalb liegt
 * sie hier und nirgends sonst (BD-8). Das Backend lädt das Paket gebaut, PWA und ERP
 * binden es über den Vite-Alias als Quelle ein.
 *
 * Der Hash läuft über `crypto.subtle` — im Browser (sicherer Kontext) und auf Node 22
 * global vorhanden. Es gibt **keinen zweiten Hash-Weg**.
 *
 * Die beiden älteren Kopien (`documents/document-posting.ts`,
 * `projects/project-tech.logic.ts`) bleiben unberührt: Ihre Hashes stehen in
 * festgeschriebenen Belegen und Leistungserklärungen und folgen anderen Regeln (keine
 * Textnormalisierung, Zahlen über `JSON.stringify`).
 *
 * **Regeln der Serialisierung** (AP-46 6.):
 * - Schlüssel sortiert (nach UTF-16-Codeeinheiten, wie `Array.prototype.sort`);
 *   `undefined` entfällt, `null` bleibt.
 * - Zahlen: nur ganze Zahlen als Zahl (`schemaVersion`, Reihenfolgen). Mengen und
 *   andere Dezimalwerte gehen als Zeichenkette mit fester Stellenzahl hinein —
 *   `festeZahl(wert, 3)`. Eine Dezimalzahl als `number` wirft: Sie hätte keine
 *   verlässliche Darstellung.
 * - Datum `YYYY-MM-DD` (`datumText`); Zeitpunkte UTC `YYYY-MM-DDTHH:mm:ssZ`
 *   (`zeitpunktText`). Ein `Date`-Objekt wird als Zeitpunkt geschrieben.
 * - Texte NFC-normalisiert, Zeilenenden `\n`, Ränder getrimmt — für **jede**
 *   Zeichenkette im Stand, auch für Schlüssel nicht (Schlüssel sind Code, kein Text).
 * - Listen in der Reihenfolge, in der sie hereinkommen. Fachliche Reihenfolge
 *   (`sortOrder`) herzustellen ist Sache dessen, der den Stand baut — nie die
 *   Einfügereihenfolge der Datenbank.
 *
 * **Fassungen:** Wer eine dieser Regeln ändert, baut eine neue Funktion mit eigener
 * Fassungsnummer (`KANONISCH_FASSUNG`) und lässt die alte stehen — alte Dokumente
 * werden mit der Funktion ihrer Fassung geprüft.
 */

/** Fassung der Serialisierung. Eine Änderung an den Regeln oben ist eine neue Fassung. */
export const KANONISCH_FASSUNG = 1

/** Text so, wie er in den Stand eingeht: NFC, `\n`, getrimmt. */
export function kanonischerText(text: string): string {
  return text.normalize('NFC').replace(/\r\n?/g, '\n').trim()
}

/**
 * Eine Zahl mit fester Stellenzahl, ohne Locale: `festeZahl(2.5, 3)` → `"2.500"`.
 *
 * Nimmt auch die Zeichenkette eines Prisma-`Decimal` (`"2.5"`). `-0` wird `0`.
 * Rundet kaufmännisch über `toFixed` — die Darstellung ist im ECMAScript-Standard
 * festgelegt und damit auf jedem Gerät gleich.
 */
export function festeZahl(wert: number | string, stellen: number): string {
  const n = typeof wert === 'number' ? wert : Number(String(wert).trim())
  if (!Number.isFinite(n)) throw new Error(`festeZahl: keine Zahl (${String(wert)})`)
  // Über die Exponentenschreibweise runden: `1.0005 * 1000` ergäbe binär 1000.4999…,
  // `Number('1.0005e3')` ist genau 1000.5. Halbe Werte runden vom Nullpunkt weg.
  const abs = Math.abs(n)
  const betrag = String(abs).includes('e')
    ? Number(abs.toFixed(stellen)) // sehr klein oder sehr groß — dort gibt es nichts auszugleichen
    : Number(`${Math.round(Number(`${abs}e${stellen}`))}e-${stellen}`)
  const text = (n < 0 && betrag !== 0 ? -betrag : betrag).toFixed(stellen)
  return text
}

/** `YYYY-MM-DD`. Ein `Date` wird nach seinen UTC-Teilen gelesen (Prisma `@db.Date`). */
export function datumText(wert: Date | string): string {
  if (typeof wert === 'string') {
    const kurz = wert.slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(kurz)) throw new Error(`datumText: kein Datum (${wert})`)
    return kurz
  }
  return wert.toISOString().slice(0, 10)
}

/** UTC-Zeitpunkt auf die Sekunde: `YYYY-MM-DDTHH:mm:ssZ`. */
export function zeitpunktText(wert: Date | string): string {
  const d = typeof wert === 'string' ? new Date(wert) : wert
  if (Number.isNaN(d.getTime())) throw new Error(`zeitpunktText: kein Zeitpunkt (${String(wert)})`)
  return `${d.toISOString().slice(0, 19)}Z`
}

/**
 * Der kanonische Text eines Stands — bitgleich auf jedem Gerät und im Server.
 *
 * Wirft bei Werten, die keine eindeutige Darstellung haben (Dezimalzahl als
 * `number`, `NaN`, Funktionen, Symbole) — lieber ein Fehler beim Einfrieren als ein
 * Hash, den der Server nie nachrechnen kann.
 */
export function kanonischesJson(wert: unknown): string {
  if (wert === null) return 'null'
  if (wert instanceof Date) return JSON.stringify(zeitpunktText(wert))
  switch (typeof wert) {
    case 'string':
      return JSON.stringify(kanonischerText(wert))
    case 'boolean':
      return wert ? 'true' : 'false'
    case 'number':
      if (!Number.isSafeInteger(wert)) {
        throw new Error(`kanonischesJson: ${wert} ist keine ganze Zahl — Dezimalwerte über festeZahl()`)
      }
      return String(Object.is(wert, -0) ? 0 : wert)
    case 'object': {
      if (Array.isArray(wert)) {
        return `[${wert.map((w) => (w === undefined ? 'null' : kanonischesJson(w))).join(',')}]`
      }
      const o = wert as Record<string, unknown>
      return `{${Object.keys(o)
        .filter((k) => o[k] !== undefined)
        .sort()
        .map((k) => `${JSON.stringify(k)}:${kanonischesJson(o[k])}`)
        .join(',')}}`
    }
    default:
      throw new Error(`kanonischesJson: Wert vom Typ ${typeof wert} hat keine Darstellung`)
  }
}

/** Die Web-Crypto-Schnittstelle — im Browser und auf Node 22 global. */
function subtle(): SubtleCrypto {
  const c = (globalThis as { crypto?: Crypto }).crypto
  if (!c?.subtle) {
    throw new Error('crypto.subtle fehlt — nur im sicheren Kontext (HTTPS) bzw. ab Node 19 verfügbar')
  }
  return c.subtle
}

/** SHA-256 hex über die UTF-8-Bytes eines Texts. */
export async function sha256Hex(daten: string | Uint8Array | ArrayBuffer): Promise<string> {
  const bytes = typeof daten === 'string' ? new TextEncoder().encode(daten) : daten
  const digest = await subtle().digest('SHA-256', bytes as BufferSource)
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

/** Inhalts-Hash über den kanonischen **Text** (nicht über das Objekt). */
export function inhaltsHash(kanonischerTextDesStands: string): Promise<string> {
  return sha256Hex(kanonischerTextDesStands)
}

/** Die ersten acht Zeichen — für die Bestätigungsanzeige (AP-46 9., Schritt 6). */
export function kurzHash(hash: string): string {
  return hash.slice(0, 8)
}
